/**
 * VoiceMinutes.jsx — Feb 9 2026.
 * Balance + buy-more page for HD TTS voice minutes.
 *
 * Stripe wiring is left as a stub (the existing payments routes can
 * later be wired to the pack ids). For now, packs show as "Coming
 * soon" so the user-facing concept is visible without a payment loop.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Volume2, ArrowLeft, Sparkles } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceMinutes = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await authFetch(`${BACKEND_URL}/api/tts/voice-minutes`);
      if (r.ok) setData(await r.json());
    })();
  }, []);

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="voice-minutes-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-5"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <header className="mb-6">
        <p className="eyebrow text-fuchsia-300">HD TTS</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight leading-[1.02] mt-1">
          Voice Minutes<span className="text-fuchsia-300">.</span>
        </h1>
        <p className="text-[11px] text-white/55 mt-1">
          Default voice is always free. HD voices use minutes from your balance.
        </p>
      </header>

      <section
        className="rounded-2xl border border-fuchsia-400/30 bg-[#12131A] p-5 mb-6"
        data-testid="voice-minutes-balance-card"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-fuchsia-400/15 border border-fuchsia-400/30 grid place-items-center">
            <Volume2 className="w-6 h-6 text-fuchsia-300" />
          </div>
          <div className="flex-1">
            <p className="eyebrow text-fuchsia-300">Current balance</p>
            <p
              className="font-display text-4xl font-black text-white tracking-tight tabular-nums mt-0.5"
              data-testid="voice-minutes-balance-value"
            >
              {(data?.balance_minutes ?? 0).toLocaleString()}
              <span className="text-base text-fuchsia-300/70 ml-2">min</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1 tabular-nums">
              Free monthly grant: {data?.free_monthly_minutes ?? 15} min · resets every month
              {data?.consumed_total > 0 && <> · {data.consumed_total} consumed lifetime</>}
            </p>
          </div>
        </div>
      </section>

      <p className="eyebrow text-amber-300 mb-3">Top up</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {(data?.packs || []).map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => {
              // Stripe wiring TODO — until then we just inform the user
              // and link to the existing payments page so admins can
              // grant minutes manually.
              window.alert('Coming soon — packs will be purchasable via Stripe. For now, ask an admin to grant minutes.');
            }}
            className="rounded-2xl border border-white/10 hover:border-fuchsia-400/50 bg-[#12131A] px-4 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400/40"
            data-testid={`voice-minutes-pack-${p.id}`}
          >
            <p className="eyebrow text-fuchsia-300">{p.label}</p>
            <p className="font-display text-2xl font-black text-white tracking-tight mt-1 tabular-nums">
              £{p.price_gbp.toFixed(2)}
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1 tabular-nums">
              +{p.minutes.toLocaleString()} min · {(p.price_gbp / p.minutes).toFixed(3)} £/min
            </p>
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-[11px] text-white/55 flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
        <p>
          You don&apos;t need minutes to read. The <strong className="text-white">Default voice</strong> works for everyone,
          and you can always <strong className="text-white">flash-read or chunk-read</strong> without spending anything.
        </p>
      </div>
    </main>
  );
};

export default VoiceMinutes;
